import * as React from "react";
import { Loader } from "../Loader";
import { ButtonWithSound } from "../AudioProvider";
import { useTranslation } from "react-i18next";
import question from "./TablesList/assets/question.png";
import infoIcon from "../../assets/info.svg";
import { useMutation } from "../../hooks/useMutation";
import {
  ClearMatchmakingQueueMutation,
  ClearMatchmakingQueueMutationVariables,
} from "../../graphql/ClearMatchmakingQueue.generated";
import { loader } from "graphql.macro";
import { useParams } from "react-router";
import { useRoom } from "./RoomProvider";
import {useShop} from "../Shop";
import {useUser} from "../UserProvider";

const LEAVE_MATCHMAKING_QUEUE_MUTATION = loader(
  "../../graphql/ClearMatchmakingQueue.graphql"
);

interface MatchingContextValue {
  toggle: (show: boolean) => void;
  isToggled: boolean;
  isHideTables: boolean;
  setIsInsufficientFunds: (value: boolean) => void;
}

const MatchingModal = ({ onClose }: { onClose: () => void }) => {
  const { sectionId } = useParams<{
    sectionId: string;
  }>();
  const { t } = useTranslation();
  const [leaveQueue, setLeaveQueue] = React.useState(false);

  const [, leaveQueueMutation] = useMutation<
    ClearMatchmakingQueueMutation,
    ClearMatchmakingQueueMutationVariables
  >(LEAVE_MATCHMAKING_QUEUE_MUTATION);

  const handleLeaveQueue = () => {
    leaveQueueMutation({ sectionId: sectionId! }).then((data) => {
      if (data?.data?.clearMatchmakingQueue) {
        onClose();
      }
    });
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.6)",
        zIndex: 999,
      }}
    >
      <div
        style={{
          width: "420px",
          background: "linear-gradient(180deg, #306DAE 0%, #063358 100%)",
          border: "1px solid #1E71D3",
          borderRadius: "20px",
          padding: "40px 32px",
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        className="text-white flex flex-col items-center gap-3"
      >
        {leaveQueue ? (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <img width={50} height={50} src={question} alt="" />
              <p className="font-semibold text-xl text-white">
                {t("matchmaking.leave_queue")}
              </p>
              <p className="text-white">
                {t("matchmaking.leave_queue_description")}
              </p>
            </div>
            <div
              className="flex gap-4"
              style={{ minWidth: "350px", margin: "0 auto" }}
            >
              <ButtonWithSound
                onClick={() => setLeaveQueue(false)}
                className="btn btn-green-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-full"
              >
                {t("matchmaking.stay")}
              </ButtonWithSound>
              <ButtonWithSound
                className="btn btn-gray-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-full"
                onClick={handleLeaveQueue}
              >
                {t("matchmaking.leave")}
              </ButtonWithSound>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-3">
              <Loader description="" />
              <p className="font-semibold text-xl text-white">
                {t("matchmaking.looking_opponent")}
              </p>
              <p className="text-white">
                {t("matchmaking.looking_opponent_description")}
              </p>
            </div>
            <ButtonWithSound
              className="btn btn-gray-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-max mx-auto"
              style={{ minWidth: "140px" }}
              onClick={() => setLeaveQueue(true)}
            >
              {t("cancel")}
            </ButtonWithSound>
          </div>
        )}
      </div>
    </div>
  );
};

const MatchingInsufficientFundsModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const {
    user: { balances },
  } = useUser();
  const openShop = useShop();

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.6)",
        zIndex: 999,
      }}
    >
      <div
        style={{
          width: "420px",
          background: "linear-gradient(180deg, #306DAE 0%, #063358 100%)",
          border: "1px solid #1E71D3",
          borderRadius: "20px",
          padding: "40px 32px",
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        className="text-white flex flex-col items-center gap-3"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <img width={50} height={50} src={infoIcon} alt=""/>
            <p className="font-semibold text-xl text-white">
              {t("matchmaking.insufficient_funds")}
            </p>
            <p className="text-white">
              {t("matchmaking.not_enough_funds")}
            </p>
          </div>
          <div
            className="flex gap-4"
            style={{minWidth: "350px", margin: "0 auto"}}
          >
            <ButtonWithSound
              onClick={() => {
                openShop(balances[0]?.currency);
                onClose();
              }}
              className="btn btn-green-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-full"
            >
              {t("xp_system.go_to_shop")}
            </ButtonWithSound>
            <ButtonWithSound
              className="btn btn-gray-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-full"
              onClick={onClose}
            >
              {t("close")}
            </ButtonWithSound>
          </div>
        </div>
      </div>
    </div>
  );
};

const MatchingContext = React.createContext<MatchingContextValue>(
  undefined as any
);

export const MatchingProvider = ({children}: React.PropsWithChildren<{}>) => {
  const room = useRoom();
  const {sectionId} = useParams<{
    sectionId: string;
  }>();

  const currentSection = room?.sections.find((s) => s.id === sectionId);

  const isAutoMatchingEnabled =
    currentSection?.__typename === "GameSection"
      ? currentSection.isAutoMatchingEnabled
      : false;

  const [machMakingToggled, setMachMakingToggled] = React.useState(false);
  const [matchingInsufficientFundsToggled, setMatchingInsufficientFundsToggled] = React.useState(false);

  const machMakingContext = {
    toggle: setMachMakingToggled,
    isToggled: machMakingToggled,
    isHideTables: isAutoMatchingEnabled,
    setIsInsufficientFunds: setMatchingInsufficientFundsToggled,

  };
  return (
    <MatchingContext.Provider value={machMakingContext}>
      {children}
      {machMakingToggled && (
        <MatchingModal onClose={() => setMachMakingToggled(false)}/>
      )}
      {matchingInsufficientFundsToggled && (
        <MatchingInsufficientFundsModal onClose={() => setMatchingInsufficientFundsToggled(false)} />
      )}
    </MatchingContext.Provider>
  );
};

export const useMatching = () => {
  return React.useContext(MatchingContext);
};
